import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { cn } from "ui";

import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { scrollWorkoutItemIntoView } from "src/workouts/workoutItemScroll";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";

import { useMinimapDragAndDropManager } from "./MinimapDragAndDropManager";
import { MinimapDropIndicator } from "./MinimapDropIndicator";
import { MinimapLinkZone } from "./MinimapLinkZone";
import { MinimapUnlinkZone } from "./MinimapUnlinkZone";
import { type MinimapInstruction } from "./minimapUtils";

interface MinimapItemBaseProps {
  id: string;
  level: number;
  index: number;
  previousItemId?: string;
  displayName: string;
  className?: string;
  children: ReactNode;
  nestedItems?: ReactNode;
}

type DragState = "idle" | "preview" | "dragging";

export const MinimapItemBase = observer(function MinimapItemBase(
  props: MinimapItemBaseProps,
) {
  const workoutEditStore = useWorkoutEditStore();
  const manager = useMinimapDragAndDropManager();
  const permissions = useWorkoutPermissions();

  const workoutItem = workoutEditStore.getWorkoutItemById(props.id);
  const [dragState, setDragState] = useState<DragState>("idle");
  const [instruction, setInstruction] = useState<MinimapInstruction | null>(
    null,
  );

  const elementRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const autoExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isGroup = workoutItem.type === "GROUP";
  const hasNestedItems = !!props.nestedItems;
  const isExpanded = hasNestedItems && manager.isExpanded(props.id);
  const isInsideSuperset = workoutEditStore.isInsideSuperset(props.id);
  const showLinkZone = props.previousItemId && !isInsideSuperset;
  const showUnlinkZone = props.previousItemId && isInsideSuperset;

  useEffect(() => {
    const element = elementRef.current;
    const dragHandle = dragHandleRef.current;

    if (!element || !dragHandle) {
      return;
    }

    return combine(
      draggable({
        element: dragHandle,
        canDrag: () => {
          return permissions.dragAndDropEnabled;
        },
        getInitialData: () => {
          return {
            id: workoutItem.id,
            type: workoutItem.type,
            isRoot: props.level === 0,
            contextId: manager.id,
          };
        },
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({ x: "8px", y: "4px" }),
            render: ({ container }) => {
              const root = createRoot(container);
              root.render(
                <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg">
                  {props.displayName}
                </div>,
              );
              return () => {
                return root.unmount();
              };
            },
          });
        },
        onDragStart: () => {
          setDragState("dragging");
          manager.startDrag(workoutItem.id);
        },
        onDrop: () => {
          setDragState("idle");
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          if (!permissions.dragAndDropEnabled) {
            return false;
          }

          return source.data.contextId === manager.id;
        },
        getData: () => {
          return { id: workoutItem.id };
        },
        getIsSticky: () => {
          return true;
        },
        onDrag: ({ source, location }) => {
          if (source.data.id === workoutItem.id) {
            setInstruction(null);
            manager.clearActiveDropTarget();
            return;
          }

          const rect = element.getBoundingClientRect();
          const y = location.current.input.clientY;
          const relativeY = y - rect.top;
          const height = rect.height;

          let newInstruction: MinimapInstruction;

          if (
            isGroup &&
            relativeY > height * 0.25 &&
            relativeY < height * 0.75
          ) {
            newInstruction = "make-child";
          } else if (relativeY < height / 2) {
            newInstruction = "reorder-above";
          } else {
            newInstruction = "reorder-below";
          }

          if (
            manager.canDropOnTarget(
              source.data.id as string,
              workoutItem.id,
              newInstruction,
            )
          ) {
            setInstruction(newInstruction);
            manager.setActiveDropTarget(workoutItem.id, newInstruction);
          } else {
            setInstruction(null);
            manager.clearActiveDropTarget();
          }

          if (
            hasNestedItems &&
            !manager.isExpanded(workoutItem.id) &&
            !autoExpandTimerRef.current
          ) {
            autoExpandTimerRef.current = setTimeout(() => {
              manager.expandItem(workoutItem.id);
              autoExpandTimerRef.current = null;
            }, 500);
          }
        },
        onDragLeave: () => {
          setInstruction(null);

          if (autoExpandTimerRef.current) {
            clearTimeout(autoExpandTimerRef.current);
            autoExpandTimerRef.current = null;
          }
        },
        onDrop: () => {
          setInstruction(null);

          if (autoExpandTimerRef.current) {
            clearTimeout(autoExpandTimerRef.current);
            autoExpandTimerRef.current = null;
          }
        },
      }),
    );
  }, [
    props.level,
    workoutItem.id,
    workoutItem.type,
    permissions.dragAndDropEnabled,
    props.displayName,
    isGroup,
    hasNestedItems,
  ]);

  function handleClick() {
    void scrollWorkoutItemIntoView({
      workoutItemId: workoutItem.id,
      block: isGroup ? "start" : "center",
    });
  }

  function handleToggleExpanded(e: React.MouseEvent) {
    e.stopPropagation();
    manager.toggleExpanded(props.id);
  }

  return (
    <div className="relative">
      {showLinkZone && props.previousItemId && permissions.canEditItems ? (
        <MinimapLinkZone
          aboveItemId={props.previousItemId}
          belowItemId={props.id}
          level={props.level}
        />
      ) : null}
      {showUnlinkZone && props.previousItemId && permissions.canEditItems ? (
        <MinimapUnlinkZone
          itemId={props.id}
          previousItemId={props.previousItemId}
          level={props.level}
        />
      ) : null}
      <div className={cn("relative", props.className)}>
        <div
          ref={elementRef}
          className={cn(
            "relative group flex items-center gap-1 py-0.5 px-1 rounded-[6px] cursor-pointer transition-colors",
            "hover:bg-slate-100/80",
            dragState === "dragging" && "opacity-40",
          )}
          style={{ paddingLeft: `${props.level * 18 + 4}px` }}
          onClick={handleClick}
        >
          {hasNestedItems ? (
            <button
              className="shrink-0 p-0 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-transform duration-150"
              onClick={handleToggleExpanded}
            >
              <Icon
                name="tabler-chevron-right"
                size={12}
                className={cn(
                  "transition-transform duration-150",
                  isExpanded && "rotate-90",
                )}
              />
            </button>
          ) : null}
          <div
            ref={dragHandleRef}
            className={cn(
              "flex items-center gap-1 flex-1 min-w-0",
              permissions.dragAndDropEnabled &&
                "cursor-grab active:cursor-grabbing",
            )}
          >
            {props.children}
          </div>

          {instruction ? (
            <MinimapDropIndicator
              instruction={instruction}
              level={props.level}
            />
          ) : null}
        </div>

        {isExpanded && props.nestedItems ? (
          <div className="flex flex-col gap-2">{props.nestedItems}</div>
        ) : null}
      </div>
    </div>
  );
});
